import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Flower2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export function AuthDialog({ open, onOpenChange, onSuccess }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (loading) return;
    setError("");
    setLoading(true);
    const res = mode === "login" ? await login(email, password) : await register(email, password, name);
    setLoading(false);
    if (res.ok) {
      onOpenChange(false);
      setEmail(""); setPassword(""); setName("");
      // Defer the follow-up action so the auth dialog's overlay fully unmounts first
      if (onSuccess) setTimeout(onSuccess, 220);
    } else {
      setError(res.error);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/profile";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#150A1A] border border-pink-500/25 text-pink-50 sm:max-w-md" data-testid="auth-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl flex items-center gap-2">
            <Flower2 className="w-6 h-6 text-pink-400" />
            {mode === "login" ? "Welcome back" : "Join the Garden"}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {mode === "login" ? "Sign in to leave notes and gift roses." : "Create an account to start planting love notes."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2" noValidate>
          {mode === "register" && (
            <div className="space-y-1.5">
              <Label className="text-pink-200 text-sm">Display name</Label>
              <Input
                data-testid="auth-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="How the Garden knows you"
                className="bg-black/40 border-pink-500/25 text-pink-50 placeholder:text-slate-500"
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-pink-200 text-sm">Email</Label>
            <Input
              data-testid="auth-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="bg-black/40 border-pink-500/25 text-pink-50 placeholder:text-slate-500"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-pink-200 text-sm">Password</Label>
            <Input
              data-testid="auth-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(e); }}
              placeholder="••••••••"
              className="bg-black/40 border-pink-500/25 text-pink-50 placeholder:text-slate-500"
            />
          </div>

          {error && <p data-testid="auth-error" className="text-rose-400 text-sm">{error}</p>}

          <button
            data-testid="auth-submit-btn"
            type="button"
            onClick={submit}
            disabled={loading}
            className="w-full bg-gradient-to-r from-pink-600 to-rose-500 hover:from-pink-500 hover:to-rose-400 text-white font-medium py-3 rounded-full shadow-lg shadow-pink-500/25 transition-all hover:scale-[1.02] disabled:opacity-60"
          >
            {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-1">
          <div className="h-px flex-1 bg-pink-500/20" />
          <span className="text-xs text-slate-500 font-mono uppercase tracking-widest">or</span>
          <div className="h-px flex-1 bg-pink-500/20" />
        </div>

        <button
          data-testid="auth-google-btn"
          onClick={googleLogin}
          className="w-full flex items-center justify-center gap-3 bg-white text-gray-800 font-medium py-3 rounded-full hover:bg-gray-100 transition-all"
        >
          <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-5 h-5" />
          Continue with Google
        </button>

        <p className="text-center text-sm text-slate-400 mt-2">
          {mode === "login" ? "New to the Garden? " : "Already a member? "}
          <button
            data-testid="auth-toggle-mode"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="text-pink-400 hover:text-pink-300 font-medium"
          >
            {mode === "login" ? "Create an account" : "Sign in"}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
}
